/**
 * Hook for user login
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@ui/use-toast';
import { loginUser, type AuthError, type LoginResult } from '@lib/api/auth';
import { toProblem } from '@lib/api/base';
import type { LoginCredentials } from '@lib/api/auth';

export const useLogin = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<LoginResult, unknown, LoginCredentials>({
    mutationFn: loginUser,
    onSuccess: ({ user, meta }) => {
      // Update user data in the cache
      queryClient.setQueryData(['currentUser'], user);

      // Invalidate queries that depend on authentication
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });

      toast({
        title: 'Login Successful',
        description: (meta?.message as string | undefined) ?? 'You have successfully logged in.',
      });
    },
    onError: error => {
      const authError = error as AuthError;
      const normalizedCode = authError.code?.toUpperCase();

      // Allow verification/no-active-company paths to be handled by callers
      if (normalizedCode === 'EMAIL_NOT_VERIFIED' || normalizedCode === 'NO_ACTIVE_COMPANY') {
        return;
      }

      const problem = toProblem(error);

      // Avoid double toasts for invalid credentials; the form handles messaging
      if (problem.code && problem.code.toUpperCase() === 'INVALID_CREDENTIALS') {
        return;
      }

      toast({
        title: 'Login Failed',
        description:
          problem.detail || problem.title || 'Failed to log in. Please check your credentials.',
        variant: 'destructive',
      });
    },
  });
};
